/** Detects whether a string contains HTML markup */
export function isHTML(s: string): boolean {
  return /<[a-z][\s\S]*>/i.test(s);
}

/**
 * Normalizes stored content to HTML for TipTap.
 * - If already HTML: passes through unchanged
 * - If legacy format ([text](url), @[name](id)): converts to HTML
 */
export function normalizeToHTML(stored: string): string {
  if (!stored) return "";
  if (isHTML(stored)) return stored;

  const mentionRegex = /@\[([^\]]+)\]\(([^)]+)\)/g;
  const hyperlinkRegex = /(?<!@)\[([^\]]+)\]\(([^)]+)\)/g;

  const tokens: Array<{
    index: number;
    length: number;
    type: "mention" | "link";
    text: string;
    target: string;
  }> = [];

  let match;
  while ((match = mentionRegex.exec(stored)) !== null) {
    tokens.push({
      index: match.index,
      length: match[0].length,
      type: "mention",
      text: match[1],
      target: match[2],
    });
  }
  while ((match = hyperlinkRegex.exec(stored)) !== null) {
    tokens.push({
      index: match.index,
      length: match[0].length,
      type: "link",
      text: match[1],
      target: match[2],
    });
  }
  tokens.sort((a, b) => a.index - b.index);

  let html = "";
  let lastIndex = 0;

  for (const token of tokens) {
    if (token.index > lastIndex) {
      html += esc(stored.substring(lastIndex, token.index));
    }
    if (token.type === "mention") {
      html += `<span data-type="mention" data-id="${escAttr(token.target)}" data-label="${escAttr(token.text)}">@${esc(token.text)}</span>`;
    } else {
      html += `<a href="${escAttr(token.target)}">${esc(token.text)}</a>`;
    }
    lastIndex = token.index + token.length;
  }

  if (lastIndex < stored.length) {
    html += esc(stored.substring(lastIndex));
  }

  // Split on double newlines for paragraphs, single newlines become <br>
  const paragraphs = html.split(/\n\n+/);
  return paragraphs.map((p) => `<p>${p.replace(/\n/g, "<br>")}</p>`).join("");
}

/**
 * Converts stored content (HTML from TipTap, or legacy [text](url) / @[name](id)
 * format) to clean plain text — suitable for copying to the clipboard.
 */
export function richContentToPlainText(content: string): string {
  if (!content) return "";

  if (isHTML(content)) {
    const withBreaks = content
      .replace(/<li[^>]*>/gi, "- ")
      .replace(/<\/(p|div|li|h[1-6]|pre|blockquote)>/gi, "\n")
      .replace(/<br\s*\/?>/gi, "\n");
    const div = document.createElement("div");
    div.innerHTML = withBreaks;
    const text = div.textContent || "";
    return text
      .replace(/[ \t]+\n/g, "\n")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  }

  // Legacy format: @[name](id) -> @name, [text](url) -> text
  return content
    .replace(/@\[([^\]]+)\]\(([^)]+)\)/g, "@$1")
    .replace(/(?<!@)\[([^\]]+)\]\(([^)]+)\)/g, "$1")
    .trim();
}

const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const escAttr = (s: string) => s.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");

/**
 * Wraps typed plain text in the shape TipTap stores.
 *
 * A composer without a rich-text editor behind it (the backlog's quick-comment
 * popover) still has to write the same content shape as the ticket page's
 * editor, or the two would render through different paths. A blank line starts
 * a new paragraph; a single newline is a hard break, which is exactly what the
 * editor's Enter / Shift+Enter produce.
 */
export function plainTextToHTML(text: string): string {
  const trimmed = text.trim();
  if (!trimmed) return "";
  return trimmed
    .split(/\n{2,}/)
    .map((para) => `<p>${esc(para).split("\n").join("<br>")}</p>`)
    .join("");
}
